import React from "react";

import { type FontWeight, type TextSize, fontWeightsMap, textSizesMap } from "../../../theme";

type BasicDoughnutChartProps = {
	/** The progress value as a percentage (0-100) */
	percent: number;
	/** The size of the doughnut chart in pixels */
	size: 96 | 168;
	/** The color of the track */
	trackColor?: string;
	/** The color of the progress bar */
	progressColor?: string;
	/** Whether to show the label */
	showLabel?: boolean;
	/** The size of the label */
	labelSize?: TextSize;
	/** The weight of the label */
	labelWeight?: FontWeight;
	/** The ref to the SVG element */
	ref?: React.Ref<SVGSVGElement>;
};
/**
 * BasicDoughnutChart - Circular Progress Indicator
 *
 * A customizable circular progress indicator that displays percentage completion
 * in a doughnut chart format with smooth animations and accessibility support.
 *
 * @example
 * ```tsx
 * <BasicDoughnutChart percent={65} size={168} />
 * ```
 */
const BasicDoughnutChart = ({
	percent,
	size,
	trackColor = "var(--py-neutral-2)",
	progressColor = "var(--py-primary-5)",
	showLabel = true,
	labelSize = "heading-3",
	labelWeight = "semi-bold",
	ref,
}: BasicDoughnutChartProps): React.ReactElement => {
	// clamp the percent and round to nearest integer
	const clampedPercent = Math.max(0, Math.min(100, percent));
	const strokeWidth = size / 10;
	const radius = (size - strokeWidth) / 2;
	// calculate the circumference of the circle
	const circumference = 2 * Math.PI * radius;
	const dashOffset = circumference * (1 - clampedPercent / 100);
	const center = size / 2;
	const trackProgressLeft = clampedPercent ? 100 - clampedPercent - 1 : 100;
	const initialTrackAngle = clampedPercent ? -182 : -180;
	const trackDashOffset = circumference * (1 - trackProgressLeft / 100);

	const labelClasses = ["text-neutral-9", textSizesMap[labelSize], fontWeightsMap[labelWeight]]
		.filter(Boolean)
		.join(" ");

	return (
		<svg
			ref={ref}
			width={size}
			height={size}
			viewBox={`0 0 ${size} ${size}`}
			role="img"
			aria-label={`Loading ${Math.floor(clampedPercent).toFixed(0)}%`}
		>
			<g transform={`rotate(-90 ${center} ${center})`}>
				<g transform={`rotate(${initialTrackAngle} ${center} ${center}) translate(${size} 0) scale(-1 1)`}>
					<circle
						cx={center}
						cy={center}
						r={radius}
						fill="none"
						stroke={trackColor}
						strokeWidth={strokeWidth}
						strokeDasharray={circumference}
						strokeDashoffset={trackDashOffset}
						style={{ transition: "stroke-dashoffset 0.4s ease" }}
					/>
				</g>
				<circle
					cx={center}
					cy={center}
					r={radius}
					fill="none"
					stroke={progressColor}
					strokeWidth={strokeWidth}
					strokeDasharray={circumference}
					strokeDashoffset={dashOffset}
					style={{ transition: "stroke-dashoffset 0.4s ease" }}
				/>
			</g>
			{showLabel ? (
				<text
					x={center}
					y={center}
					textAnchor="middle"
					dominantBaseline="middle"
					className={labelClasses}
					fill="currentColor"
				>
					{Math.floor(clampedPercent).toFixed(0)}%
				</text>
			) : null}
		</svg>
	);
};

BasicDoughnutChart.displayName = "BasicDoughnutChart";

export { BasicDoughnutChart, type BasicDoughnutChartProps };
